import React from "react";

interface MatchScoreBadgeProps {
  score: number;
}

function getScoreColor(score: number): { ring: string; text: string; bg: string } {
  if (score >= 75) return { ring: "stroke-green-500", text: "text-green-700", bg: "bg-green-50" };
  if (score >= 50) return { ring: "stroke-amber-500", text: "text-amber-700", bg: "bg-amber-50" };
  return { ring: "stroke-red-500", text: "text-red-700", bg: "bg-red-50" };
}

function getScoreLabel(score: number): string {
  if (score >= 75) return "Strong match";
  if (score >= 50) return "Partial match";
  return "Low match";
}

const MatchScoreBadge: React.FC<MatchScoreBadgeProps> = ({ score }) => {
  const clampedScore = Math.max(0, Math.min(100, Math.round(score)));
  const colors = getScoreColor(clampedScore);
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (clampedScore / 100) * circumference;

  return (
    <div className={`inline-flex flex-col items-center gap-1 rounded-xl px-6 py-4 ${colors.bg}`}>
      <div className="relative h-24 w-24">
        <svg className="h-24 w-24 -rotate-90" viewBox="0 0 100 100">
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth="8"
            className="text-gray-200"
          />
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            strokeWidth="8"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            strokeLinecap="round"
            className={colors.ring}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-2xl font-bold leading-none ${colors.text}`}>
            {clampedScore}
          </span>
          <span className={`text-xs ${colors.text} opacity-75`}>/ 100</span>
        </div>
      </div>
      <span className={`text-sm font-semibold ${colors.text}`}>{getScoreLabel(clampedScore)}</span>
    </div>
  );
};

export default MatchScoreBadge;
